import {Component, computed, inject, signal} from "@angular/core";
import {CommonModule} from "@angular/common";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatButtonModule} from "@angular/material/button";
import {RouterModule} from "@angular/router";
import {UserDataService} from "src/app/features/admin/data-access/user-data.service";
import {WorkstationService} from "src/app/features/admin/data-access/workstation.service";
import {BootstrapIcon} from "src/app/services/header.service";
import {Userrole} from "src/app/shared/interfaces/user";
import {EditDeleteButtonsComponent} from "../../../components/edit-delete-buttons/edit-delete-buttons.component";
import {StorageService} from "src/app/shared/data-access/storage.service";
import {ProductionLineService} from "src/app/features/admin/data-access/production-line.service";
import {environment} from "../../../../../../environments/environment";

type SortCriteria = "name" | "role" | "status" | null;

interface SortState {
  criteria: SortCriteria;
  ascending: boolean;
}

@Component({
  selector: "app-user-view",
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonModule,
    RouterModule,
    EditDeleteButtonsComponent,
  ],
  templateUrl: "./user-view.component.html",
  styleUrls: ["./user-view.component.scss"],
})
export class UserViewComponent {
  userDataService = inject(UserDataService);
  workstationService = inject(WorkstationService);
  productionLineService = inject(ProductionLineService);
  // inject storageService so that userroles are already loaded when navigating to input form
  storageService = inject(StorageService);
  BootstrapIcon = BootstrapIcon;
  Userrole = Userrole;
  isProduction = signal<boolean>(environment.production);
  sortState = signal<SortState>({criteria: null, ascending: true});

  findProductionLine(productionLine: string) {
    return this.productionLineService
      .productionLines()
      .find((line) => line.id === productionLine)?.name;
  }

  sortedUsers = computed(() => {
    const users = this.userDataService.users();
    const {criteria, ascending} = this.sortState();

    if (!criteria) return users;

    return [...users].sort((a, b) => {
      let comparison = 0;
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();

      switch (criteria) {
        case "name":
          comparison = nameA.localeCompare(nameB);
          break;

        case "role":
          comparison = a.userrole
            .toLowerCase()
            .localeCompare(b.userrole.toLowerCase());
          if (comparison === 0) {
            comparison = nameA.localeCompare(nameB);
          }
          break;

        case "status":
          if (a.isActive === b.isActive) {
            comparison = (a.isActive ? "Aktiv" : "Inaktiv").localeCompare(
              b.isActive ? "Aktiv" : "Inaktiv"
            );
          } else {
            comparison = a.isActive ? -1 : 1;
          }
          break;
      }

      return ascending ? comparison : -comparison;
    });
  });

  // Click handlers for the header
  toggleSort(criteria: SortCriteria) {
    const currentState = this.sortState();

    if (currentState.criteria === criteria) {
      // Same criteria clicked - reverse the order
      this.sortState.set({criteria, ascending: !currentState.ascending});
    } else {
      // New criteria - set to ascending first
      this.sortState.set({criteria, ascending: true});
    }
  }
}
